import {
  ConsumerDiaryIcon,
  CreateStudyIcon,
  FocusGroupIcon,
  ImportResearchIcon,
  ProductTestingIcon,
} from '@/components/Home/NewSurveyModal/Icons';
import usePoller from '@/hooks/usePoller';
import {
  getOperationStatus,
  markOperationAsNotified,
} from '@/store/asyncActions/dashboardActions';
import { AppDispatch, RootState } from '@/store/index';
import {
  dashboardActions,
  OperationStatusObj,
} from '@/store/Reducers/dashboardReducer';
import { LoadingOutlined } from '@ant-design/icons';
import { UserIcon } from '@heroicons/react/outline';
import { Badge, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { CrossIcon } from '../Icons';
import { OPERATION_STATUS_MESSAGE } from './constants';
import CustomErrorBoundary from '@/components/CustomErrorBoundry';
import { Notification01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Tabs from 'ui/tabs';
import { Card, CardHeader, CardBody, ScrollShadow } from '@heroui/react';
import Chip from 'ui/chip';
import PopoverBase from 'ui/popover';

dayjs.extend(utc);
dayjs.extend(timezone);

const OPERATION_STATUS_INTERVAL = 1000 * 2 * 60;

const StudyTypeIcon = {
  survey: <CreateStudyIcon color={'black'} width={16} height={16} />,
  video_diary: <ConsumerDiaryIcon color={'black'} width={16} height={16} />,
  long_format: <FocusGroupIcon color={'black'} width={16} height={16} />,
  product_testing: (
    <ProductTestingIcon color={'black'} width={16} height={16} />
  ),
  excel: <ImportResearchIcon color={'black'} width={30} height={16} />,
};

const COLORS = [
  '#FAEDCB', // Light Yellow
  '#C9E4DE', // Light Teal
  '#C6DEF1', // Light Blue
  '#DBCDF0', // Light Purple
  '#F2C6DE', // Light Pink
  '#F7D9C4', // Light Peach
];

const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'in progress':
      return 'processing';
    case 'failed':
      return 'error';
    default:
      return 'processing';
  }
};

const OperationRow = ({ operation }: { operation: OperationStatusObj }) => {
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  const isCompleted = ['FAILED', 'COMPLETED', 'DONE'].includes(
    operation.status,
  );
  const BatchIcon =
    StudyTypeIcon[operation.researchType as keyof typeof StudyTypeIcon];
  const color = stringToColor(operation.batchTitle);

  const showQuestionIndex = operation.operationName === 'SHORT_THEME';

  return (
    <div
      key={operation.id ?? operation.operationGroupId}
      className="bg-white border-b border-enum-border-color space-y-2 pb-3 px-6"
    >
      <span className="text-primary-black font-semibold text-md">
        {operation.message}
      </span>
      <div className="flex items-center  text-xs rounded-md text-gray-500">
        <div className="pr-2">
          {isCompleted
            ? dayjs
                .utc(operation.createdAt)
                .tz(dayjs.tz.guess())
                .format('D MMMM, hh:mm a')
            : dayjs
                .utc(operation.createdAt)
                .tz(dayjs.tz.guess())
                .format('hh:mm a')}
        </div>
        <div className="text-divider mx-0.5">|</div>
        <div className="pl-1 flex items-center gap-1">
          <UserIcon color={'#6b7280'} width={12} height={12} />
          {operation.createdBy}
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-2">
        {/* Batch Title */}
        {operation.batchTitle && (
          <Tooltip title={operation.batchTitle} placement="topRight">
            <div
              className=" text-xs rounded-md px-2 py-1"
              style={{ backgroundColor: color }}
            >
              <div className="flex items-center gap-1">
                {BatchIcon}
                {operation.batchTitle.slice(0, 40) +
                  (operation.batchTitle.length > 40 ? '...' : '')}
              </div>
            </div>
          </Tooltip>
        )}

        {/* Index / Questions */}
        {!!operation?.isOverall === false && showQuestionIndex && (
          <div className="flex items-center flex-wrap gap-2">
            {operation.index && (
              <>
                {(showAllQuestions
                  ? operation.index
                  : operation.index.slice(0, 2)
                ).map((question: string, idx: number) => (
                  <div
                    key={idx}
                    className="text-xs rounded-md px-2 py-1"
                    style={{ backgroundColor: '#E0D7F1' }}
                  >
                    Q:{question}
                  </div>
                ))}

                {/* +N or Collapse button */}
                {operation.index.length > 2 &&
                  (!showAllQuestions ? (
                    <div
                      onClick={() => setShowAllQuestions(true)}
                      className="text-default-text-color text-xs rounded-md px-2 py-1 cursor-pointer bg-gray-200 hover:bg-gray-300"
                    >
                      +{operation.index.length - 2}
                    </div>
                  ) : (
                    <div
                      onClick={() => setShowAllQuestions(false)}
                      className="text-default-text-color text-xs rounded-md px-2 py-1 cursor-pointer"
                    >
                      <CrossIcon color={'black'} />
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
      </div>

      {!isCompleted && (
        <>
          <div className="py-2.5">
            <div className="w-full bg-enum-border-color rounded-full h-1">
              <div
                className={`h-1 rounded-full transition-all duration-500 ease-in-out ${
                  operation.completedValue > 0 ? 'bg-link-blue' : 'bg-gray-300'
                }`}
                style={{ width: `${operation.completedValue}%` }}
              ></div>
            </div>
          </div>

          <div className="w-full flex justify-between items-center">
            <span className="flex items-center gap-2">
              <Spin
                indicator={
                  <LoadingOutlined
                    spin
                    className="text-link-blue "
                    style={{ fontSize: 16, strokeWidth: 16 }}
                  />
                }
              />
              <span className="first-letter:capitalize lowercase text-sm">
                {getOperationMessage(operation)}
              </span>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

type OperationStatusProps = {
  children?: React.ReactNode;
};

const getOperationMessage = (operation: OperationStatusObj) => {
  const operationStatus =
    OPERATION_STATUS_MESSAGE[
      operation.operationName as keyof typeof OPERATION_STATUS_MESSAGE
    ];

  return (
    operationStatus[operation.status as keyof typeof operationStatus] ??
    operationStatus['DEFAULT']
  );
};

const OperationStatus: React.FC<OperationStatusProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentTab, setCurrentTab] = useState('ongoing');

  const { operations, notificationCount, isDrawerOpen } = useSelector(
    (state: RootState) => ({
      operations: state.dashboardReducer.operationStatusDrawer.operations,
      notificationCount:
        state.dashboardReducer.operationStatusDrawer.notificationCount,
      isDrawerOpen: state.dashboardReducer.operationStatusDrawer.isDrawerOpen,
    }),
    shallowEqual,
  );

  const { progressingOperations, completedOperations } = useMemo(() => {
    return operations.reduce(
      (
        acc: {
          progressingOperations: OperationStatusObj[];
          completedOperations: OperationStatusObj[];
        },
        operation: OperationStatusObj,
      ) => {
        if (!['FAILED', 'COMPLETED', 'DONE'].includes(operation.status)) {
          acc.progressingOperations.push(operation);
        } else {
          acc.completedOperations.push(operation);
        }
        return acc;
      },
      { progressingOperations: [], completedOperations: [] },
    );
  }, [operations]);

  const [startPolling, stopPolling] = usePoller(
    () => fetchOperations(),
    OPERATION_STATUS_INTERVAL,
  );

  const fetchOperations = async () => {
    try {
      await dispatch(getOperationStatus());
    } catch (error) {
      console.error('Error fetching operation status:', error);
    }
    // const operations = res.payload.operations;

    // stop polling if all operations are completed or failed
    // if (operations.every((operation: OperationStatusObj) => ['COMPLETED', 'FAILED'].includes(operation.status))) {
    //   stopPolling();
    // }
  };

  useEffect(() => {
    fetchOperations();

    return () => {
      dispatch(dashboardActions.clearOperationStatus());
      stopPolling();
    };
  }, []);

  // mark operation as notified when drawer is opened
  useEffect(() => {
    if (notificationCount > 0 && isDrawerOpen && currentTab === 'completed') {
      const completedOperations =
        operations?.filter(
          (operation: OperationStatusObj) =>
            !operation.isNotified &&
            ['COMPLETED', 'FAILED', 'DONE']?.includes(operation.status),
        ) ?? [];

      const operationIds = completedOperations
        .map((operation: OperationStatusObj) => operation.id)
        .filter((id: number | undefined) => !!id);
      const operationGroupIds = completedOperations
        .map((operation: OperationStatusObj) => operation.operationGroupId)
        .filter((id: number | undefined) => !!id);

      if (operationIds.length > 0 || operationGroupIds.length > 0) {
        markOperationAsNotified({ operationIds, operationGroupIds })
          .then(() => {
            dispatch(dashboardActions.setOperationStatusNotificationCount(0));
          })
          .catch(() => {
            // handle error
            console.error('Error marking operation as notified');
          });
      }
    }
  }, [notificationCount, isDrawerOpen, currentTab]);

  // start polling when there is an operation in progress
  useEffect(() => {
    const inProgressOperation =
      operations?.filter(
        (operation: OperationStatusObj) =>
          !['COMPLETED', 'FAILED', 'DONE']?.includes(operation.status),
      ) ?? [];
    if (inProgressOperation.length > 0) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [operations]);

  // clear operations when drawer is closed
  useEffect(() => {
    if (isDrawerOpen) {
      fetchOperations();
    }
  }, [isDrawerOpen]);

  return (
    <CustomErrorBoundary
      fallback={
        <Badge count={'!'} color="#6b83e7">
          <div className="cursor-pointer px-1 my-auto text-primary-text-color">
            <HugeiconsIcon
              icon={Notification01Icon}
              size={20}
              color="currentColor"
              strokeWidth={1.5}
            />
          </div>
        </Badge>
      }
    >
      <PopoverBase
        onClose={() => {
          dispatch(dashboardActions.setOperationStatusDrawerOpen(false));
        }}
        overrideDefaultStyles={true}
        trigger={
          <div className="flex items-center justify-center p-1 cursor-pointer text-primary-text-color  hover:bg-background-color rounded-md">
            <Badge count={notificationCount} color="#6b83e7">
              <div
                onClick={() =>
                  dispatch(dashboardActions.setOperationStatusDrawerOpen(true))
                }
              >
                <HugeiconsIcon
                  icon={Notification01Icon}
                  size={20}
                  color="currentColor"
                  strokeWidth={1.5}
                />
              </div>
            </Badge>
          </div>
        }
        children={
          <Card className="w-full min-w-[400px]">
            <CardHeader className="flex flex-col px-0 pb-0">
              <div className="flex w-full items-center justify-between px-5 py-2">
                <div className="inline-flex items-center gap-1">
                  <h4 className="text-large inline-block align-middle font-medium">
                    Notifications
                  </h4>
                </div>
              </div>
              <div className="w-full">
                <Tabs
                  items={[
                    {
                      title: (
                        <div className="flex items-center space-x-2">
                          <span>On Going Operations </span>
                          <Chip size="sm" variant="flat" color="primary">
                            {progressingOperations.length}
                          </Chip>
                        </div>
                      ),
                      key: 'ongoing',
                    },
                    {
                      title: (
                        <div className="flex items-center space-x-2">
                          <span>Completed Operations</span>
                          <Chip size="sm" variant="flat" color="primary">
                            {completedOperations.length}
                          </Chip>
                        </div>
                      ),
                      key: 'completed',
                    },
                  ]}
                  value={currentTab}
                  onChange={(value) => {
                    setCurrentTab(value);
                  }}
                  size="md"
                  color="primary"
                  variant="underlined"
                  classNames={{
                    base: 'w-full',
                    tabList:
                      'py-0 px-4 w-full relative rounded-none border-b border-divider',
                    cursor: 'w-full',
                    tab: 'max-w-fit h-12',
                  }}
                />
              </div>
            </CardHeader>
            <CardBody className="w-full gap-0 p-0 mt-4">
              <ScrollShadow className="h-[500px] w-full overflow-y-scroll scrollbar-thin">
                {/* Ongoing Operations Section */}
                {currentTab === 'ongoing' &&
                  (progressingOperations.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex-col w-full space-y-2 items-center justify-center">
                        {progressingOperations.map(
                          (operation: OperationStatusObj, index: number) => (
                            <OperationRow key={index} operation={operation} />
                          ),
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-secondary-black py-4 px-6">
                      No ongoing operations to display
                    </div>
                  ))}
                {/* Completed Operations Section */}
                {currentTab === 'completed' &&
                  (completedOperations.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex-col w-full space-y-2 items-center justify-center">
                        {completedOperations.map(
                          (operation: OperationStatusObj, index: number) => (
                            <OperationRow key={index} operation={operation} />
                          ),
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-secondary-black py-4 px-6">
                      No completed operations to display
                    </div>
                  ))}
              </ScrollShadow>
            </CardBody>
          </Card>
        }
        placement="bottom-end"
      ></PopoverBase>
    </CustomErrorBoundary>
  );
};

export default OperationStatus;
